import React from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import {
  palette,
  spacing,
  radius,
  typography,
  transition,
  shadow,
  layout,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';

/**
 * THE BACK BUTTON. One design, one component, one position.
 *
 * ═══════════════ WHY THIS EXISTS ═══════════════
 * Owner instruction, 2026-08-11: "the back buttons are all over the place, make
 * a unified design for a back button and everywhere it appears it should be on
 * the top right." Before this the product shipped FIVE different treatments of
 * the same act:
 *
 *   · <ScreenHeader>'s pill, top right          (parcel desk, visitor exit)
 *   · <NavButton direction="back">, bottom left (daily log, roster, report,
 *                                                visitor log entry)
 *   · a bare "← Back to units" text link        (unit detail, staff scope)
 *   · <GhostButton>Back to menu</GhostButton>   (IQ handover)
 *   · an anchor styled as body copy             (account page)
 *
 * Five shapes in five places for one action, each in a different corner. A
 * reader learns where "out of here" lives once; anything else makes them hunt.
 *
 * ═══════════════ THE POSITION IS PART OF THE CONTRACT ═══════════════
 * TOP LEFT, always. Owner instruction, 2026-08-11 (revising the top-right call
 * made earlier the same day): "back button should go on the left EVERYWHERE."
 * Left is where a reader's eye starts and where every browser, phone OS and
 * native app puts the same control, so the product now matches the platform
 * instead of fighting it.
 *
 * <ScreenHeader> puts it there for screens with a title — on its own row ABOVE
 * the title, because the title row is left-anchored by the heading itself and a
 * back button competing for that spot would push the heading around. <BackBar>
 * is the standalone left-aligned row for screens without a header. A page that
 * renders <BackButton> loose in its own layout has broken the rule this
 * component exists to hold — use one of the two containers.
 *
 * ═══════════════ IT GOES SOMEWHERE REAL, NEVER navigate(-1) ═══════════════
 * `to` is an explicit route. A concierge who opened the screen from a deep link,
 * a refresh, or a fresh tab has no in-app history entry, so a history offset
 * would walk them out of the application entirely — and on the first screen of a
 * session it does nothing at all, which reads as a dead button. An explicit path
 * lands in the same place every time.
 *
 * `replace: false` is deliberate and spelled out: the screen stays on the stack
 * so the browser's own back button still behaves as the reader expects.
 *
 * ═══════════════ WHAT THIS IS NOT ═══════════════
 * It is NOT the wizard's step-back. The visitor-log and daily-log flows pair a
 * "Back" with a "Next" at the foot of each step, and that pair is one control
 * for moving through a sequence — splitting it across two corners would break
 * the rhythm of the flow. Those keep <NavButton direction="back">. This button
 * is for LEAVING a screen, which is a different act.
 *
 * No aurora anywhere: gradient.aurora is reserved for Lobbero IQ. A back button
 * is not AI.
 */

const Chevron: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M10 3.5 5.5 8l4.5 4.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const BackButtonBase = styled.button`
  -webkit-appearance: none;
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  /* 44px, not the 36px compact control height the old header pill used. This is
     frequently the only way off a screen on a desk tablet, and the design
     system's touch floor is not negotiable for the control that gets somebody
     unstuck. */
  min-height: 44px;
  padding: 0 ${spacing.sm};
  border-radius: ${radius.pill};
  background: transparent;
  border: 1px solid ${palette.line};
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  white-space: nowrap;
  cursor: pointer;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    border-color: ${palette.lineStrong};
    color: ${palette.inkSoft};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/**
 * The standalone container for a screen with no <ScreenHeader>.
 *
 * `justify-content: flex-start` is the whole job: it is what makes "top left"
 * true on a page that has nothing else in the row. Rendered as the FIRST child
 * of the screen's content so it sits above the content rather than beside it.
 *
 * Every screen that shows a standalone back control goes through this one rule,
 * which is why the left/right decision was a single line to change rather than
 * a sweep through fifteen pages.
 *
 * ── `$gutter`: FOR HOSTS THAT PAD NOTHING ────────────────────────────────
 *
 * The bar carries NO inset by default, and that is deliberate: on a padded host
 * the back button and the page title must share one left edge, so a padding of
 * its own would push it out of line with the heading it sits above.
 *
 * Two screens host it in a container with `padding: 0` — /roster and
 * /view-daily-report both render <ContentAndButtonsContainer dashboard>, whose
 * dashboard variant is deliberately full-bleed. Every OTHER block on those pages
 * brings its own gutter (PickerBar, ButtonsWrapper, ContentContainer), so the
 * back button alone sat hard in the corner of the card at 0,0 while the content
 * under it started 32px in — reported 2026-08-14.
 *
 * `$gutter` gives the bar that page's own gutter instead, so it lines up with
 * what follows it and clears the card edge on the top and both sides; the
 * bottom gap is the margin the bar already carries. The VALUE names the host's
 * gutter rather than being one fixed number, because the two pages do not use
 * the same one — matching the wrong one just moves the misalignment.
 */
export const BackBar = styled.div<{ $gutter?: 'md' | 'lg' }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${spacing.xs};
  box-sizing: border-box;
  width: 100%;
  margin-bottom: ${spacing.sm};

  ${({ $gutter }) => {
    if (!$gutter) return '';
    const pad = $gutter === 'lg' ? spacing.lg : spacing.md;

    return `padding: ${pad} ${pad} 0;`;
  }}

  ${down('md')} {
    ${({ $gutter }) =>
      $gutter
        ? `padding: ${layout.gutterMobile} ${layout.gutterMobile} 0;`
        : ''}
  }
`;

export interface BackButtonProps {
  /** Where back goes. An explicit route — never a history offset. */
  to: string;
  /** The label. Name the destination ("Back to units") rather than saying only
   *  "Back" wherever the destination is not obvious from the screen itself. */
  label?: string;
  disabled?: boolean;
  /** Overrides the navigation entirely. For the handful of callers that must
   *  close a local view rather than change route — a drawer, a wizard step that
   *  is genuinely a page. Prefer `to`. */
  onClick?: () => void;
}

export const BackButton: React.FC<BackButtonProps> = ({
  to,
  label = 'Back',
  disabled,
  onClick,
}) => {
  const navigate = useNavigate();

  return (
    <BackButtonBase
      type="button"
      disabled={disabled}
      onClick={() => {
        if (onClick) {
          onClick();

          return;
        }
        navigate(to, { replace: false });
      }}
    >
      <Chevron />
      {label}
    </BackButtonBase>
  );
};
